// Positioned text from a PDF page, grouped into lines. Pure: no DOM, no PDF library. The PDF reader
// produces TextItems; the parser works on Lines so it can reason about columns and what sits below a label.

/** One run of text as the PDF places it. x, y are the baseline origin in page points (y grows upward). */
export interface TextItem {
  str: string;
  x: number;
  y: number;
  /** Width of the run in page points. */
  w: number;
  /** Font size in page points. */
  h: number;
  /** 1-based page number. */
  page: number;
}

export interface Span {
  start: number;
  end: number;
  item: TextItem;
}

/** Items that share a baseline, left to right, joined into one string. */
export interface Line {
  page: number;
  y: number;
  h: number;
  text: string;
  spans: Span[];
}

/** Group items into lines in reading order: page by page, top to bottom, left to right. */
export function buildLines(items: TextItem[]): Line[] {
  const byPage = new Map<number, TextItem[]>();
  for (const it of items) {
    if (it.str.trim() === "" && it.str !== " ") continue;
    const list = byPage.get(it.page) ?? [];
    list.push(it);
    byPage.set(it.page, list);
  }
  const out: Line[] = [];
  for (const page of [...byPage.keys()].sort((a, b) => a - b)) {
    const sorted = byPage.get(page)!.sort((a, b) => b.y - a.y || a.x - b.x);
    const clusters: TextItem[][] = [];
    for (const it of sorted) {
      const last = clusters[clusters.length - 1];
      if (last) {
        const ref = last[0]!;
        const tol = Math.max(2, 0.45 * Math.max(it.h, ref.h));
        if (Math.abs(it.y - ref.y) <= tol) {
          last.push(it);
          continue;
        }
      }
      clusters.push([it]);
    }
    for (const c of clusters) out.push(toLine(c, page));
  }
  return out;
}

function toLine(cluster: TextItem[], page: number): Line {
  const items = cluster.filter((i) => i.str.trim() !== "").sort((a, b) => a.x - b.x);
  const spans: Span[] = [];
  let text = "";
  let prev: TextItem | null = null;
  for (const it of items) {
    if (prev) {
      const gap = it.x - (prev.x + prev.w);
      // A clear gap, or an overlap (two runs placed independently), means separate words.
      const needsSpace = !/\s$/.test(prev.str) && !/^\s/.test(it.str) && (gap > 1.5 || gap < -2);
      if (needsSpace) text += " ";
    }
    const start = text.length;
    text += it.str;
    spans.push({ start, end: text.length, item: it });
    prev = it;
  }
  const y = cluster.reduce((s, i) => s + i.y, 0) / cluster.length;
  const h = Math.max(...cluster.map((i) => i.h));
  return { page, y, h, text, spans };
}

/** Approximate x position of a character in a line, interpolated within its text run. */
export function xAt(line: Line, index: number): number {
  for (const s of line.spans) {
    if (index >= s.start && index < s.end) {
      const len = s.end - s.start;
      return s.item.x + (len ? ((index - s.start) / len) * s.item.w : 0);
    }
    if (index < s.start) return s.item.x;
  }
  const last = line.spans[line.spans.length - 1];
  return last ? last.item.x + last.item.w : 0;
}

/**
 * Text of one column on a line: the items that start inside [xFrom, xTo), stopping at the first gap wide
 * enough to be another column.
 */
export function columnText(line: Line, xFrom: number, xTo: number, maxGap = 40): string {
  const items = line.spans.filter((s) => s.item.x >= xFrom && s.item.x < xTo).map((s) => s.item);
  const kept: TextItem[] = [];
  for (const it of items) {
    const prev = kept[kept.length - 1];
    if (prev && it.x - (prev.x + prev.w) > maxGap) break;
    kept.push(it);
  }
  return kept
    .map((i) => i.str)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}
